import { useLazyGetChartDetailsQuery } from '@api/chart';
import {
  useUpdateDynamicFiltersMutation,
  useUpdateProjectBoardFiltersMutation
} from '@api/projects';
import {
  CloseButton,
  CustomSelect,
  FaIcon,
  FilterItem,
  MDBox,
  MDButton,
  MDTypography
} from '@components';
import {
  Grid,
  Tooltip,
  Switch,
  Button,
  Dialog,
  DialogContent,
  Link
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { otherDataTypes } from '@utils/common';
import {
  ColumnItemDynamicFilter,
  ColumnItemList,
  ColumnItemRange,
  FilterType,
  IGlobalFilters,
  isActiveFilter
} from '@DStypes/filter';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentProjectData } from '@pages/ProjectBoard/slice';
import { sendAmplitudeData } from '@utils/amplitude';
import {
  ADDED_DASHBOARD_FILTER,
  CLICKED_DASHBOARD_FILTER,
  CLICKED_KNOW_MORE,
  REMOVED_DASHBOARD_FILTER
} from '@constants/eventNames';
import { numberEquivalents, textEquivalents } from '@constants/common';
import { MSG_EVENT, MSG_TYPE, NotificationData } from '@DStypes/notifications';
import dayjs from 'dayjs';
import { setUserNotifications } from '@components/Notification/slice';
import { RootState } from '@store/store';
import { getFilterTooltipText } from '@utils/chart';

export interface MarkType {
  value: number;
  label: string;
}
interface Props {
  filters: IGlobalFilters;
  dynamic_filters: IGlobalFilters;
}

const PBFilter: React.FC<Props> = ({ filters, dynamic_filters }) => {
  const { projectId } = useParams();
  const dispatch = useDispatch();

  const projectData = useSelector(
    (state: RootState) => state?.projectBoard?.currentProjectData
  );

  const [getChartDetails, { data: dropDownData }] =
    useLazyGetChartDetailsQuery();

  const [updateProjectBoardFilters] = useUpdateProjectBoardFiltersMutation();
  const [updateDynamicFilters] = useUpdateDynamicFiltersMutation();

  const [projectBoardFilters, setProjectBoardFilters] = useState(filters);
  const [dynamicFilters, setDynamicFilters] = useState(dynamic_filters);

  const [isClearAllFiltersModalOpen, setClearAllFiltersModalOpen] =
    useState(false);

  const filterColumns = useMemo(
    () => Object.keys(projectBoardFilters),
    [projectBoardFilters]
  );

  const dynamicFilterColumns = useMemo(
    () => Object.keys(dynamicFilters),
    [dynamicFilters]
  );

  const [filterToggle, setFilterToggle] = useState(
    filterColumns?.length > 0 || dynamicFilterColumns.length > 0 ? true : false
  );
  const [addNewFilter, setAddNewFilter] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState('');

  useEffect(() => {
    if (!filterColumns.length && !dynamicFilterColumns.length)
      setAddNewFilter(true);
  }, [filterColumns, dynamicFilterColumns]);

  useEffect(() => {
    if (projectId) getChartDetails({ id: projectId as string });
  }, [projectId]);

  const dropDownOptions = useMemo(() => {
    if (dropDownData) {
      const filterColumns = Object.keys(projectBoardFilters);
      const dynamicFilterColumns = Object.keys(dynamicFilters);

      const allFilters = filterColumns.concat(dynamicFilterColumns);

      const options = dropDownData?.available_fields
        ?.map((obj: any) => {
          if (!allFilters.includes(obj.label)) return obj;
        })
        .filter((obj: any) => obj !== undefined);

      return options;
    }

    return [];
  }, [projectBoardFilters, dynamicFilters, dropDownData]);

  const getColumnType = (columnName: string) => {
    if (dropDownData && dropDownData?.available_fields) {
      const column = dropDownData.available_fields.find(
        (obj: any) => obj.label === columnName
      );

      return column?.type;
    }
  };

  const sendNotification = (msg_event: MSG_EVENT, message: string) => {
    const payload: NotificationData = {
      created_at: dayjs(new Date())
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      id: dayjs(new Date()).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      is_read: false,
      is_silent: true,
      is_user_message: true,
      message_event: msg_event,
      message_text: message,
      updated_at: dayjs(new Date())
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      message_type: MSG_TYPE.INFO,
      message_data: JSON.stringify({
        project: { id: projectId, name: projectData?.project_name }
      }),
      linkKeyword: 'here.',
      replaceKeyword: '<click here>',
      is_important: false,
      recipient_obj: 0,
      isAnchorElement: true
    };

    dispatch(setUserNotifications(payload));
  };

  const handleFilterSubmit = (
    newFilter: IGlobalFilters,
    dynamic_filter: boolean
  ) => {
    setAddNewFilter(false);
    setSelectedColumn('');

    const columnType = getColumnType(Object.keys(newFilter)[0]);

    sendAmplitudeData(ADDED_DASHBOARD_FILTER, {
      'Data Type': textEquivalents.includes(columnType)
        ? 'TEXT'
        : numberEquivalents.includes(columnType)
        ? 'NUMBER'
        : 'DATE',
      'Filter Count(before)': filterColumns.length
    });

    if (dynamic_filter) {
      if (filterColumns.includes(Object.keys(newFilter)[0]))
        handleDeleteFilter(Object.keys(newFilter)[0], false, false);

      const modifiedFilter = { ...dynamicFilters, ...newFilter };

      setDynamicFilters(modifiedFilter);
      dispatch(setCurrentProjectData({ dynamic_filters: modifiedFilter }));
      updateDynamicFilters({
        project_id: projectId,
        payload: { dynamic_filters: modifiedFilter }
      });
    } else {
      if (dynamicFilterColumns.includes(Object.keys(newFilter)[0]))
        handleDeleteFilter(Object.keys(newFilter)[0], false, true);

      const modifiedFilter = { ...projectBoardFilters, ...newFilter };

      setProjectBoardFilters(modifiedFilter);
      dispatch(setCurrentProjectData({ filters: modifiedFilter }));
      updateProjectBoardFilters({
        project_id: projectId,
        payload: { filters: modifiedFilter }
      });
    }
    sendNotification(
      MSG_EVENT.FILTERING_IN_PROGRESS,
      'Adding Filter to Dashboard... Please wait. This may take up to a minute. Avoid refreshing or canceling. Thank you!'
    );
  };

  const handleDeleteFilter = (
    key: string,
    newFilterItem: boolean,
    dynamic_filter: boolean
  ) => {
    if (newFilterItem) {
      if (filterColumns.length > 0 && dynamicFilterColumns.length > 0)
        setAddNewFilter(false);
      setSelectedColumn('');

      return;
    }

    const columnType = getColumnType(key);

    sendAmplitudeData(REMOVED_DASHBOARD_FILTER, {
      'Data Type': textEquivalents.includes(columnType)
        ? 'TEXT'
        : numberEquivalents.includes(columnType)
        ? 'NUMBER'
        : 'DATE'
    });

    if (dynamic_filter) {
      const currentFilter = { ...dynamicFilters };

      delete currentFilter[key];
      setDynamicFilters(currentFilter);
      dispatch(setCurrentProjectData({ dynamic_filters: currentFilter }));
      updateDynamicFilters({
        project_id: projectId,
        payload: { dynamic_filters: currentFilter }
      });
    } else {
      const currentFilter = { ...projectBoardFilters };

      delete currentFilter[key];
      setProjectBoardFilters(currentFilter);
      dispatch(setCurrentProjectData({ filters: currentFilter }));
      updateProjectBoardFilters({
        project_id: projectId,
        payload: { filters: currentFilter }
      });
    }
  };

  const handleColumnToggleChange = (
    value: boolean,
    columnName: string,
    dynamic_filter: boolean
  ) => {
    if (dynamic_filter) {
      const modifiedFilter = {
        ...dynamicFilters,
        [columnName]: {
          ...dynamicFilters[columnName],
          active: value ? isActiveFilter.TRUE : isActiveFilter.FALSE
        }
      };

      setDynamicFilters(modifiedFilter);
      dispatch(setCurrentProjectData({ dynamic_filters: modifiedFilter }));

      updateDynamicFilters({
        project_id: projectId,
        payload: { dynamic_filters: modifiedFilter }
      });
    } else {
      const modifiedFilter = {
        ...projectBoardFilters,
        [columnName]: {
          ...projectBoardFilters[columnName],
          active: value ? isActiveFilter.TRUE : isActiveFilter.FALSE
        }
      };

      setProjectBoardFilters(modifiedFilter);
      dispatch(setCurrentProjectData({ filters: modifiedFilter }));

      updateProjectBoardFilters({
        project_id: projectId,
        payload: { filters: modifiedFilter }
      });
    }
  };

  const getColumnName = (id: string) => {
    if (dropDownData && dropDownData?.available_fields) {
      const column = dropDownData.available_fields.find(
        (obj: any) => obj.value === id
      );

      return column.label;
    }
  };

  const getColumnId = (name: string) => {
    if (dropDownData.available_fields) {
      const column = dropDownData.available_fields.find(
        (obj: any) => obj.label === name
      );

      return column?.value;
    }
  };

  const getFilterType = (id: string) => {
    const column = dropDownData?.available_fields?.find(
      (obj: any) => obj.value === id
    );

    if (column?.type === 'TEXT' || otherDataTypes.includes(column?.type))
      return FilterType.VALUES;

    return FilterType.RANGE;
  };

  const getIsDateValue = (id: string) => {
    const column = dropDownData?.available_fields?.find(
      (obj: any) => obj.value === id
    );

    if (column?.type === 'DATE' || column?.type === 'DATETIME')
      return column?.type;
    else return '';
  };

  const handleClearAllFilters = () => {
    setFilterToggle(false);
    setSelectedColumn('');
    if (filterColumns.length > 0 || dynamicFilterColumns.length > 0) {
      const dataTypes = filterColumns.map((column: string) => {
        const columnType = getColumnType(column);

        if (textEquivalents.includes(columnType)) return 'TEXT';
        else if (numberEquivalents.includes(columnType)) return 'NUMBER';
        else return 'DATE';
      });

      const dynamicFilterTypes = dynamicFilterColumns.map(() => 'DATE');

      sendAmplitudeData(REMOVED_DASHBOARD_FILTER, {
        'Data Type': (dataTypes as string[])
          .concat(dynamicFilterTypes)
          .join(', ')
      });

      setProjectBoardFilters({});
      setDynamicFilters({});
      dispatch(setCurrentProjectData({ filters: {} }));
      dispatch(setCurrentProjectData({ dynamic_filters: {} }));
      updateProjectBoardFilters({
        project_id: projectId,
        payload: { filters: {} }
      });
      updateDynamicFilters({
        project_id: projectId,
        payload: { dynamic_filters: {} }
      });
    }
    setClearAllFiltersModalOpen(false);
  };

  const handleAddFilterToggle = (value: boolean) => {
    if (value) {
      sendAmplitudeData(CLICKED_DASHBOARD_FILTER);
      setFilterToggle(value);
    } else if (
      !value &&
      (filterColumns.length > 0 || dynamicFilterColumns.length > 0)
    ) {
      setClearAllFiltersModalOpen(true);
    } else {
      setFilterToggle(false);
      setSelectedColumn('');
    }
  };

  return (
    <MDBox width='100%' maxWidth='100%'>
      <MDBox height={`${200}px`}>
        <Grid container justifyContent='space-between' my={1}>
          <Grid item>
            <Tooltip title={''} placement='top'>
              <Switch
                checked={filterToggle}
                onChange={(e, value) => handleAddFilterToggle(value)}
              />
            </Tooltip>
            <MDTypography variant='sm' color='info' ml={1} mr={0.5}>
              Add a Filter
            </MDTypography>
          </Grid>
          <Grid item>
            <MDTypography
              variant='sm'
              color='secondary'
              sx={{
                textAlign: 'center',
                textUnderlineOffset: 4.5
              }}
              component={Link}
              href='https://datasquirrel.notion.site/How-to-Filter-data-on-any-column-ea549696ac7f4788ad0ff9bc917ae6e1'
              target='_blank'
              rel='noreferrer'
              underline
              fontWeight='medium'
              textGradient
            >
              <span
                role='presentation'
                onClick={() =>
                  sendAmplitudeData(CLICKED_KNOW_MORE, {
                    'Feature Name': 'Filter'
                  })
                }
              >
                How to use?
              </span>
            </MDTypography>
          </Grid>
        </Grid>
        {filterToggle && dropDownData && (
          <>
            {filterColumns?.map((column: string, index: number) => (
              <FilterItem
                key={column}
                index={index + 1}
                isDashboardFilter={true}
                iconTooltipText={getFilterTooltipText(
                  projectData?.filters || {},
                  projectData?.dynamic_filters || {},
                  dropDownData?.available_fields,
                  false,
                  '',
                  projectData?.date_range
                )}
                projectId={projectId as string}
                columnId={getColumnId(column)}
                columnName={column}
                filterType={(projectBoardFilters[column] as any).type}
                columnsIncluded={
                  (projectBoardFilters[column] as ColumnItemList)?.values
                }
                sliderValues={[
                  (projectBoardFilters[column] as ColumnItemRange)?.startValue,
                  (projectBoardFilters[column] as ColumnItemRange)?.endValue
                ]}
                handleFilterSubmit={handleFilterSubmit}
                handleDeleteFilter={handleDeleteFilter}
                handleColumnToggleChange={handleColumnToggleChange}
                activeStatus={projectBoardFilters[column].active}
                isDate={getIsDateValue(getColumnId(column))}
              />
            ))}
            {dynamicFilterColumns?.map((column: string, index: number) => (
              <FilterItem
                key={column}
                index={index + 1 + filterColumns.length}
                isDashboardFilter={true}
                iconTooltipText={getFilterTooltipText(
                  projectData?.filters || {},
                  projectData?.dynamic_filters || {},
                  dropDownData?.available_fields,
                  false,
                  '',
                  projectData?.date_range
                )}
                projectId={projectId as string}
                columnId={getColumnId(column)}
                columnName={column}
                filterType={FilterType.DYNAMIC}
                handleFilterSubmit={handleFilterSubmit}
                handleDeleteFilter={handleDeleteFilter}
                handleColumnToggleChange={handleColumnToggleChange}
                activeStatus={dynamicFilters[column].active}
                isDate={'DATE'}
                dynamicFilterType={
                  (dynamicFilters[column] as ColumnItemDynamicFilter).filter
                }
                integerDateLimit={
                  (dynamicFilters[column] as ColumnItemDynamicFilter)?.number_of
                }
                dateGroupChoice={
                  (dynamicFilters[column] as ColumnItemDynamicFilter)
                    ?.group_choice
                }
                baselineChoice={
                  (dynamicFilters[column] as ColumnItemDynamicFilter)?.baseline
                }
              />
            ))}
            {!addNewFilter && (
              <MDBox
                display='flex'
                component={Button}
                alignItems='center'
                disableRipple
                justifyContent='center'
                rowGap={3}
                bordered
                gap='10px'
                sx={({ palette }) => ({
                  borderColor: palette.secondary.main,
                  py: 0.5,
                  width: '100%',
                  my: 2
                })}
                onClick={() => {
                  setAddNewFilter(true);
                  sendAmplitudeData(CLICKED_DASHBOARD_FILTER);
                }}
              >
                <FaIcon
                  icon='plus'
                  typographyProps={{ variant: 'xs', color: 'secondary' }}
                />
                <MDTypography variant='sm' color='secondary' fontWeight='bold'>
                  Select another column and Filter
                </MDTypography>
              </MDBox>
            )}
            {addNewFilter && !selectedColumn && (
              <MDBox>
                <CustomSelect
                  placeholder='Select Column Name'
                  color='white'
                  value={selectedColumn}
                  sx={{
                    width: '100%',
                    mb: 2,
                    zIndex: 5000
                  }}
                  handleValueChange={(e: any) =>
                    setSelectedColumn(e.target.value)
                  }
                  options={dropDownOptions}
                />
              </MDBox>
            )}
          </>
        )}
        {filterToggle && addNewFilter && selectedColumn && (
          <FilterItem
            isDashboardFilter={true}
            isNewFilterItem={true}
            projectId={projectId as string}
            iconTooltipText={getFilterTooltipText(
              projectData?.filters || {},
              projectData?.dynamic_filters || {},
              dropDownData?.available_fields,
              false,
              '',
              projectData?.date_range
            )}
            columnId={selectedColumn}
            columnName={getColumnName(selectedColumn)}
            filterType={getFilterType(selectedColumn)}
            columnsIncluded={[]}
            handleFilterSubmit={handleFilterSubmit}
            handleDeleteFilter={handleDeleteFilter}
            activeStatus={isActiveFilter.TRUE}
            isDate={getIsDateValue(selectedColumn)}
          />
        )}
      </MDBox>

      <Dialog
        open={isClearAllFiltersModalOpen}
        sx={{
          '& .MuiPaper-root': {
            width: '700px',
            maxWidth: '800px',
            position: 'relative',
            padding: '16px',
            overflow: 'visible'
          }
        }}
      >
        <CloseButton
          onClose={() => {
            setClearAllFiltersModalOpen(false);
          }}
        />
        <DialogContent>
          <MDBox marginBottom={2}>
            {`${filterColumns.concat(dynamicFilterColumns).join(', ')} ${
              filterColumns.length + dynamicFilterColumns.length > 1
                ? 'filters'
                : 'filter'
            } will be removed from all chart(s), except
            for those with individual filters applied.`}
            <br />
            To proceed, click OK
          </MDBox>
          <MDBox display='flex' width='100%' justifyContent='center' gap={3}>
            <MDButton color='primary' onClick={handleClearAllFilters}>
              OK
            </MDButton>
            <MDButton
              color='primary'
              onClick={() => {
                setClearAllFiltersModalOpen(false);
              }}
            >
              CANCEL
            </MDButton>
          </MDBox>
        </DialogContent>
      </Dialog>
    </MDBox>
  );
};

export default PBFilter;
